import { useTranslations } from '@i18n/useLanguage'
import { useCallback, useEffect, useState } from 'react'
import { Button } from '@app/components/core/button/Button'
import { Title } from '@app/components/core/typography/Title'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { EditItem } from '../shared/EditItem'
import { useAuthState, useClient, useQuery } from '@based/sdk/react'
import { Text } from '@app/components/core/typography/Text'
import { ProfileEdit } from 'functions/app/dashboard/profile/edit'
import { Select } from '@app/components/core/input/select/Select'
import { EditButton } from '@app/components/core/button/EditButton'
import { PersonalProfileModel } from 'functions/app/dashboard/profile/view'

export const LivingSituationEditPanel = ({
  profile,
}: {
  profile: PersonalProfileModel
}) => {
  const authState = useAuthState()
  const t = useTranslations()
  const translations = t.app.dashboard.preferences
  const commonTranslations = t.app.common
  const [editMode, setEditMode] = useState(false)
  const [housingType, setHousingType] = useState(profile?.housingTypePreference)
  const [ownership, setOwnership] = useState(profile?.ownershipPreference)
  const [purchaseBudget, setPurchaseBudget] = useState(
    profile?.purchaseBudgetPreference,
  )
  const [rentalBudget, setRentalBudget] = useState(
    profile?.rentalBudgetPreference,
  )
  const [spaceSize, setSpaceSize] = useState(profile?.spaceSizePreference)
  const [involvement, setInvolvement] = useState(profile?.involvementPreference)

  const client = useClient()

  // Fetch preference options from database
  const [housingTypeOptions, setHousingTypeOptions] = useState<
    Array<{ id: number; name: string }>
  >([])
  const [ownershipOptions, setOwnershipOptions] = useState<
    Array<{ id: number; name: string }>
  >([])
  const [spaceSizeOptions, setSpaceSizeOptions] = useState<
    Array<{ id: number; name: string }>
  >([])
  const [involvementOptions, setInvolvementOptions] = useState<
    Array<{ id: number; name: string }>
  >([])

  useEffect(() => {
    const closeHT = client
      .query('list-housing-form-type')
      .subscribe((r: any) => {
        setHousingTypeOptions(Array.isArray(r) ? r : [])
      })
    const closeO = client
      .query('list-ownership-situation')
      .subscribe((r: any) => {
        setOwnershipOptions(Array.isArray(r) ? r : [])
      })
    const closeSS = client.query('list-house-surface').subscribe((r: any) => {
      setSpaceSizeOptions(Array.isArray(r) ? r : [])
    })
    const closeI = client.query('list-involvement').subscribe((r: any) => {
      setInvolvementOptions(Array.isArray(r) ? r : [])
    })
    return () => {
      closeHT()
      closeO()
      closeSS()
      closeI()
    }
  }, [client])

  const purchaseBudgetQuery = useQuery('list-purchase-budget')
  const rentBudgetQuery = useQuery('list-rent-budget')

  const purchaseBudgetOptions: { id: number; title: string }[] =
    purchaseBudgetQuery.data ?? []
  const rentBudgetOptions: { id: number; name: string }[] =
    rentBudgetQuery.data ?? []

  const reset = useCallback(() => {
    setHousingType(profile.housingTypePreference)
    setOwnership(profile.ownershipPreference)
    setPurchaseBudget(profile.purchaseBudgetPreference)
    setRentalBudget(profile.rentalBudgetPreference)
    setSpaceSize(profile.spaceSizePreference)
    setInvolvement(profile.involvementPreference)
    setEditMode(false)
  }, [profile])

  const save = useCallback(() => {
    const value: ProfileEdit = {
      housingTypePreference:
        housingType !== null ? { id: housingType.id } : null,
      ownershipPreference: ownership !== null ? { id: ownership.id } : null,
      purchaseBudgetPreference:
        purchaseBudget !== null ? { id: purchaseBudget.id } : null,
      rentalBudgetPreference:
        rentalBudget !== null ? { id: rentalBudget.id } : null,
      spaceSizePreference: spaceSize !== null ? { id: spaceSize.id } : null,
      involvementPreference:
        involvement !== null ? { id: involvement.id } : null,
    }
    void client
      .call('edit-profile', { id: authState.userId, value })
      .then(() => setEditMode(false))
  }, [
    setEditMode,
    authState,
    housingType,
    ownership,
    purchaseBudget,
    rentalBudget,
    spaceSize,
    involvement,
    client,
  ])

  useEffect(() => {
    setHousingType(profile.housingTypePreference)
    setOwnership(profile.ownershipPreference)
    setPurchaseBudget(profile.purchaseBudgetPreference)
    setRentalBudget(profile.rentalBudgetPreference)
    setSpaceSize(profile.spaceSizePreference)
    setInvolvement(profile.involvementPreference)
  }, [profile])

  const emptyLabel = commonTranslations.notSpecified

  return (
    <Column
      alignItems="stretch"
      style={{
        backgroundColor: 'var(--palette-white)',
        padding: '1.5rem',
        borderRadius: '1.25rem',
        flex: '1 1 20rem',
        minWidth: '20rem',
      }}
    >
      <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Title.H4 style={{ marginBottom: 0 }}>
          {translations.livingSituationTitle}
        </Title.H4>
        <EditButton isVisible={!editMode} onClick={() => setEditMode(true)} />
      </Row>
      <Column gap={'1rem'} alignItems="stretch">
        <Text.Medium>{translations.livingSituationDescription}</Text.Medium>
        <EditItem
          label={translations.housingType}
          editMode={editMode}
          displayValue={housingType ? housingType.name : emptyLabel}
        >
          <Select
            label={translations.housingType}
            value={housingType?.id}
            setValue={(value) =>
              setHousingType(value !== null && value !== undefined ? { id: value, name: '' } : null)
            }
            notSelectedLabel={emptyLabel}
            options={housingTypeOptions.map((item) => ({
              value: item.id,
              label: item.name,
            }))}
          />
        </EditItem>
        <EditItem
          label={translations.ownershipType}
          editMode={editMode}
          displayValue={ownership ? ownership.name : emptyLabel}
        >
          <Select
            label={translations.ownershipType}
            value={ownership?.id}
            setValue={(value) =>
              setOwnership(value !== null && value !== undefined ? { id: value, name: '' } : null)
            }
            notSelectedLabel={emptyLabel}
            options={ownershipOptions.map((item) => ({
              value: item.id,
              label: item.name,
            }))}
          />
        </EditItem>
        {/* TODO: since ownership is db entity and not a code constant 
        we cannot with certainty rely on its values to display either purchaseBudget or rentalBudget.
        Figure out what to do with that */}
        <EditItem
          label={translations.purchaseBudget}
          editMode={editMode}
          displayValue={purchaseBudget ? purchaseBudget.title : emptyLabel}
        >
          <Select
            label={translations.purchaseBudget}
            value={purchaseBudget?.id}
            setValue={(value) =>
              setPurchaseBudget(value !== null && value !== undefined ? { id: value, title: '' } : null)
            }
            notSelectedLabel={emptyLabel}
            options={purchaseBudgetOptions.map((item) => ({
              value: item.id,
              label: item.title,
            }))}
          />
        </EditItem>
        <EditItem
          label={translations.rentalBudget}
          editMode={editMode}
          displayValue={rentalBudget ? rentalBudget.name : emptyLabel}
        >
          <Select
            label={translations.rentalBudget}
            value={rentalBudget?.id}
            setValue={(value) =>
              setRentalBudget(value !== null && value !== undefined ? { id: value, name: '' } : null)
            }
            notSelectedLabel={emptyLabel}
            options={rentBudgetOptions.map((item) => ({
              value: item.id,
              label: item.name,
            }))}
          />
        </EditItem>
        <EditItem
          label={translations.spaceSize}
          editMode={editMode}
          displayValue={spaceSize ? spaceSize.name : emptyLabel}
        >
          <Select
            label={translations.spaceSize}
            value={spaceSize?.id}
            setValue={(value) =>
              setSpaceSize(value !== null && value !== undefined ? { id: value, name: '' } : null)
            }
            notSelectedLabel={emptyLabel}
            options={spaceSizeOptions.map((item) => ({
              value: item.id,
              label: item.name,
            }))}
          />
        </EditItem>
        <EditItem
          label={translations.involvement}
          editMode={editMode}
          displayValue={involvement ? involvement.name : emptyLabel}
        >
          <Select
            label={translations.involvement}
            value={involvement?.id}
            setValue={(value) =>
              setInvolvement(value !== null && value !== undefined ? { id: value, name: '' } : null)
            }
            notSelectedLabel={emptyLabel}
            options={involvementOptions.map((item) => ({
              value: item.id,
              label: item.name,
            }))}
          />
        </EditItem>
      </Column>
      {editMode && (
        <Row style={{ justifyContent: 'flex-end', paddingTop: '1rem' }}>
          <Button
            variant="secondary"
            label={commonTranslations.cancel}
            onClick={reset}
          />
          <Button
            variant="primary"
            label={commonTranslations.save}
            onClick={save}
          />
        </Row>
      )}
    </Column>
  )
}
